// src/pages/AdminUsers.jsx

import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  ShieldCheck,
  BadgeDollarSign,
  Headset,
  Lock,
  LockOpen,
  Pencil,
  KeyRound,
  Trash2,
  User,
  Search,
  Zap,
  FolderKanban,
  Save,
  Info,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { adminUsersApi, menuApi, securityApi } from "../../services/api";
import {
  Badge,
  Table,
  PageHeader,
  StatCard,
  Modal,
  Input,
  Button,
  Toggle,
  Select,
} from "../../components/ui";
import toast from "react-hot-toast";

const ROLE_OPTIONS = [
  { value: "", label: "All Roles" },
  { value: "R1", label: "Super Admin" },
  { value: "R2", label: "Finance" },
  { value: "R3", label: "Support" },
];

const ROLE_SELECT_OPTIONS = [
  { value: "R1", label: "Super Admin" },
  { value: "R2", label: "Finance" },
  { value: "R3", label: "Support" },
];

const ALL_ROLES = ["ROLE_SUPER_ADMIN", "ROLE_FINANCE", "ROLE_SUPPORT"];

const ROLE_ICONS = {
  ROLE_SUPER_ADMIN: ShieldCheck,
  ROLE_FINANCE: BadgeDollarSign,
  ROLE_SUPPORT: Headset,
};

const ROLE_LABELS = {
  ROLE_SUPER_ADMIN: "Super Admin",
  ROLE_FINANCE: "Finance",
  ROLE_SUPPORT: "Support",
};

const ROLE_ID_TO_FULL = {
  R1: "ROLE_SUPER_ADMIN",
  R2: "ROLE_FINANCE",
  R3: "ROLE_SUPPORT",
};

const FULL_TO_ROLE_ID = {
  ROLE_SUPER_ADMIN: "R1",
  ROLE_FINANCE: "R2",
  ROLE_SUPPORT: "R3",
};

const toShortId = (role) => FULL_TO_ROLE_ID[role] || role || "R2";
const toFullRole = (role) => ROLE_ID_TO_FULL[role] || role || "ROLE_FINANCE";

const ROLE_BADGE_VARIANT = { R1: "gold", R2: "info", R3: "default" };

const STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "LOCKED", label: "Locked" },
];

const getStatus = (row) => {
  const s = typeof row?.status === "string" ? row.status.trim().toUpperCase() : "";

  if (s === "LOCKED") return "LOCKED";
  if (s === "ACTIVE") return "ACTIVE";
  if (s === "INACTIVE") return "INACTIVE";

  if (row?.locked === true || row?.isLocked === true) return "LOCKED";
  if (row?.active === true || row?.isActive === true) return "ACTIVE";
  if (row?.active === false || row?.isActive === false) return "INACTIVE";

  return "INACTIVE";
};

const SUPER_ADMIN_ONLY_PATHS = ["/admin-users", "/admin/users", "/admin-users/*"];

const renderRoleIcon = (role, props = {}) => {
  const Icon = ROLE_ICONS[role];
  if (!Icon) return null;
  return <Icon size={14} strokeWidth={2} {...props} />;
};

function HeaderDropdown({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const handleOutside = (e) => {
      if (
        btnRef.current &&
        !btnRef.current.contains(e.target) &&
        panelRef.current &&
        !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleScrollOrResize = () => {
      setOpen(false);
    };

    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [open]);

  const handleToggle = (e) => {
    e.stopPropagation();

    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX + rect.width / 2,
      });
    }

    setOpen((o) => !o);
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleToggle}
        title="Filter"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 22,
          height: 22,
          borderRadius: 4,
          cursor: "pointer",
          padding: 0,
          border: value ? "1px solid rgba(15,61,62,0.6)" : "1px solid var(--border)",
          background: value ? "rgba(15,61,62,0.12)" : "transparent",
          color: value ? "#0F3D3E" : "var(--text-muted)",
          transition: "all 0.18s",
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="11"
          height="11"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.8}
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.18s",
          }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: "absolute",
              top: pos.top,
              left: pos.left,
              transform: "translateX(-50%)",
              zIndex: 99999,
              minWidth: 150,
              width: "max-content",
              background: "#0F3D3E",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 10,
              boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
              overflow: "hidden",
            }}
          >
            {options.map((opt) => {
              const isActive = opt.value === value;

              return (
                <div
                  key={String(opt.value)}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.10)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isActive
                      ? "rgba(255,255,255,0.12)"
                      : "transparent";
                  }}
                  style={{
                    padding: "9px 14px",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? "#fff" : "rgba(255,255,255,0.75)",
                    background: isActive ? "rgba(255,255,255,0.12)" : "transparent",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    transition: "background 0.15s",
                    letterSpacing: "0.3px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {opt.label}
                  {isActive && (
                    <svg
                      style={{ marginLeft: "auto" }}
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="#fff"
                      strokeWidth={3}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
}

function Avatar({ name, roleId }) {
  const shortId = toShortId(roleId);
  const colors = { R1: "#0F3D3E", R2: "#1a5f7a", R3: "#4a5568" };

  return (
    <div
      style={{
        width: 30,
        height: 30,
        borderRadius: "50%",
        background: colors[shortId] || "#0F3D3E",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontSize: 11,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {(name || "?").slice(0, 1).toUpperCase()}
    </div>
  );
}

function RoleManagementSection({ admin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fullRole = toFullRole(admin.roleId);
  const roleName = ROLE_LABELS[fullRole] || fullRole;
  const isSuperAdmin = fullRole === "ROLE_SUPER_ADMIN";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await menuApi.getAll();
      setItems(r.data.data || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleRole = async (item) => {
    const has = (item.allowedRoles || []).includes(fullRole);
    const updated = {
      ...item,
      allowedRoles: has
        ? item.allowedRoles.filter((r) => r !== fullRole)
        : [...(item.allowedRoles || []), fullRole],
    };
    try {
      await menuApi.update(item.id, updated);
      toast.success(has ? "Access removed" : "Access granted");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const sorted = [...items].sort((a, b) => a.order - b.order);
  const visible = isSuperAdmin
    ? sorted
    : sorted.filter(
        (item) =>
          !SUPER_ADMIN_ONLY_PATHS.some(
            (p) => item.path === p || item.path?.startsWith(p.replace("/*", ""))
          )
      );

  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: 10,
        overflow: "hidden",
        marginTop: 16,
      }}
    >
      <div
        style={{
          background: "rgba(15,61,62,0.06)",
          borderBottom: "1px solid var(--border)",
          padding: "10px 14px",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <FolderKanban size={15} strokeWidth={2} color="var(--text-primary)" />
        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-primary)" }}>
          Menu Access
        </span>
        <span
          style={{
            fontSize: 11,
            color: "var(--text-muted)",
            background: "var(--border)",
            borderRadius: 20,
            padding: "1px 8px",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {renderRoleIcon(fullRole, { size: 12, strokeWidth: 2 })}
          {roleName}
        </span>
        <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-muted)" }}>
          {visible.filter((i) => (i.allowedRoles || []).includes(fullRole)).length}/{visible.length} granted
        </span>
      </div>

      <div style={{ padding: "12px 14px", background: "var(--bg-card)" }}>
        {loading ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                style={{
                  height: 38,
                  borderRadius: 8,
                  background: "var(--border)",
                  opacity: 0.5,
                  animation: "pulse 1.5s ease-in-out infinite",
                }}
              />
            ))}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {visible.map((item) => {
              const assigned = (item.allowedRoles || []).includes(fullRole);

              return (
                <div
                  key={item.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: `1px solid ${
                      assigned ? "rgba(15,61,62,0.25)" : "var(--border)"
                    }`,
                    background: assigned ? "rgba(15,61,62,0.06)" : "var(--bg-input)",
                    gap: 8,
                    transition: "all 0.15s",
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: assigned ? "#0F3D3E" : "var(--text-secondary)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.title}
                    </span>
                    {assigned && (
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: "#0F3D3E",
                          background: "rgba(15,61,62,0.12)",
                          borderRadius: 20,
                          padding: "1px 6px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          flexShrink: 0,
                        }}
                      >
                        Active
                      </span>
                    )}
                  </div>

                  <div style={{ flexShrink: 0 }}>
                    <Toggle value={assigned} onChange={() => toggleRole(item)} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function SecuritySection({ admin }) {
  const fullRole = toFullRole(admin.roleId);

  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ipInput, setIpInput] = useState("");

  useEffect(() => {
    securityApi
      .getAll()
      .then((r) => {
        const all = r.data.data || [];
        const found = all.find((p) => p.role === fullRole);
        const defaults = {
          role: fullRole,
          require2FA: false,
          allow2FA: true,
          requireIpWhitelist: false,
          allowedIps: [],
          maxSessionDurationMs: 3600000,
          passwordExpiryDays: 90,
        };
        const resolved = found ? { ...defaults, ...found } : defaults;
        setPolicy(resolved);
        setIpInput((resolved.allowedIps || []).join("\n"));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [fullRole]);

  const save = async () => {
    setSaving(true);
    try {
      const ips = ipInput
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      await securityApi.upsert({ ...policy, allowedIps: ips });
      toast.success("Security policy saved");
    } catch {
      toast.error("Failed to save");
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div
        style={{
          border: "1px solid var(--border)",
          borderRadius: 10,
          marginTop: 16,
          padding: 16,
          color: "var(--text-muted)",
          fontSize: 12,
        }}
      >
        Loading security policy...
      </div>
    );
  }

  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: 10,
        overflow: "hidden",
        marginTop: 16,
      }}
    >
      <div
        style={{
          background: "rgba(220,38,38,0.05)",
          borderBottom: "1px solid var(--border)",
          padding: "10px 14px",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Lock size={15} strokeWidth={2} color="var(--text-primary)" />
        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-primary)" }}>
          Security Policy
        </span>
        <span
          style={{
            fontSize: 11,
            color: "var(--text-muted)",
            background: "var(--border)",
            borderRadius: 20,
            padding: "1px 8px",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {renderRoleIcon(fullRole, { size: 12, strokeWidth: 2 })}
          {ROLE_LABELS[fullRole] || fullRole}
        </span>
      </div>

      <div
        style={{
          padding: "14px",
          background: "var(--bg-card)",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {[
          ["require2FA", "Require 2FA"],
          ["allow2FA", "Allow 2FA"],
          ["requireIpWhitelist", "IP Whitelist"],
        ].map(([k, l]) => (
          <div
            key={k}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 10px",
              background: "var(--bg-input)",
              borderRadius: 8,
              border: "1px solid var(--border)",
            }}
          >
            <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{l}</span>
            <Toggle value={!!policy[k]} onChange={(v) => setPolicy({ ...policy, [k]: v })} />
          </div>
        ))}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Input
            label="Password Expiry (days)"
            type="number"
            value={policy.passwordExpiryDays || 90}
            onChange={(e) =>
              setPolicy({ ...policy, passwordExpiryDays: +e.target.value })
            }
          />
          <Input
            label="Max Session (ms)"
            type="number"
            value={policy.maxSessionDurationMs || 3600000}
            onChange={(e) =>
              setPolicy({ ...policy, maxSessionDurationMs: +e.target.value })
            }
          />
        </div>

        {policy.requireIpWhitelist && (
          <div>
            <label
              style={{
                fontSize: 11,
                color: "var(--text-muted)",
                fontWeight: 600,
                display: "block",
                marginBottom: 6,
              }}
            >
              ALLOWED IPs
            </label>
            <textarea
              rows={3}
              placeholder={"192.168.1.1\n10.0.0.50"}
              value={ipInput}
              onChange={(e) => setIpInput(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 10px",
                borderRadius: 8,
                border: "1px solid var(--border)",
                background: "var(--bg-input)",
                color: "var(--text-primary)",
                fontSize: 12,
                fontFamily: "monospace",
                resize: "none",
                outline: "none",
              }}
            />
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button size="sm" onClick={save} disabled={saving}>
            {saving ? (
              "Saving..."
            ) : (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Save size={14} strokeWidth={2} />
                Save Policy
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

function EditAdminModal({ admin, onClose, onSuccess }) {
  const currentStatus = getStatus(admin);

  const [form, setForm] = useState({
    name: admin.name || "",
    email: admin.email || "",
    roleId: toShortId(admin.roleId),
  });
  const [status, setStatus] = useState(currentStatus);
  const [toggling, setToggling] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminUsersApi.update(admin.id, {
        name: form.name,
        email: form.email,
        roleId: toFullRole(form.roleId),
      });
      toast.success("Admin updated");
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error");
    }
    setLoading(false);
  };

  const handleToggleActive = async () => {
    const newActive = status !== "ACTIVE";
    setToggling(true);
    try {
      await adminUsersApi.updateStatus(admin.id, newActive);
      setStatus(newActive ? "ACTIVE" : "INACTIVE");
      toast.success(newActive ? "Account activated" : "Account deactivated");
      onSuccess();
    } catch {
      toast.error("Failed to update status");
    }
    setToggling(false);
  };

  const handleToggleLock = async () => {
    const newLocked = status !== "LOCKED";
    setToggling(true);
    try {
      await adminUsersApi.updateLock(admin.id, newLocked);
      setStatus(newLocked ? "LOCKED" : "ACTIVE");
      toast.success(newLocked ? "Account locked" : "Account unlocked");
      onSuccess();
    } catch {
      toast.error("Failed to update lock");
    }
    setToggling(false);
  };

  const isActive = status === "ACTIVE";
  const isLocked = status === "LOCKED";

  return (
    <Modal open={!!admin} onClose={onClose} title={`Edit Admin — ${admin.name}`} width={780}>
      <div style={{ maxHeight: "72vh", overflowY: "auto", paddingRight: 2 }}>
        <div style={{ border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
          <div
            style={{
              background: "rgba(15,61,62,0.06)",
              borderBottom: "1px solid var(--border)",
              padding: "10px 14px",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <User size={15} strokeWidth={2} color="var(--text-primary)" />
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-primary)" }}>
              Basic Information
            </span>
          </div>

          <form
            onSubmit={handleSave}
            style={{
              padding: "14px",
              display: "flex",
              flexDirection: "column",
              gap: 12,
              background: "var(--bg-card)",
            }}
          >
            <Input
              label="Full Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
            <Select
              label="Role"
              value={form.roleId}
              onChange={(e) => setForm({ ...form, roleId: e.target.value })}
              options={ROLE_SELECT_OPTIONS}
            />

            <div
              style={{
                borderTop: "1px solid var(--border)",
                paddingTop: 12,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.7px",
                }}
              >
                Account Status
              </span>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 12px",
                  background: "var(--bg-input)",
                  borderRadius: 8,
                  border: `1px solid ${isActive ? "rgba(15,61,62,0.25)" : "var(--border)"}`,
                  opacity: toggling ? 0.6 : 1,
                  transition: "opacity 0.15s",
                }}
              >
                <div>
                  <div style={{ fontSize: 13, color: "var(--text-primary)", fontWeight: 600 }}>
                    Active
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      {isActive ? (
                        <CheckCircle2 size={13} strokeWidth={2} color="#16a34a" />
                      ) : (
                        <XCircle size={13} strokeWidth={2} color="#dc2626" />
                      )}
                      {isActive ? "User can log in" : "User cannot log in"}
                    </span>
                  </div>
                </div>
                <Toggle value={isActive} onChange={handleToggleActive} disabled={toggling} />
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 12px",
                  background: "var(--bg-input)",
                  borderRadius: 8,
                  border: `1px solid ${isLocked ? "rgba(220,38,38,0.25)" : "var(--border)"}`,
                  opacity: toggling ? 0.6 : 1,
                  transition: "opacity 0.15s",
                }}
              >
                <div>
                  <div style={{ fontSize: 13, color: "var(--text-primary)", fontWeight: 600 }}>
                    Locked
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      {isLocked ? (
                        <Lock size={13} strokeWidth={2} color="#dc2626" />
                      ) : (
                        <LockOpen size={13} strokeWidth={2} color="#16a34a" />
                      )}
                      {isLocked ? "Account is locked" : "Account is accessible"}
                    </span>
                  </div>
                </div>
                <Toggle value={isLocked} onChange={handleToggleLock} disabled={toggling} />
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <Button variant="ghost" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </div>

        <RoleManagementSection admin={admin} />
        <SecuritySection admin={admin} />
      </div>
    </Modal>
  );
}

function ConfirmDialog({ open, name, onConfirm, onCancel }) {
  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          background: "var(--bg-card)",
          borderRadius: 14,
          padding: 28,
          maxWidth: 400,
          width: "100%",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          border: "1px solid var(--border)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: "rgba(239,68,68,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              fill="none"
              viewBox="0 0 24 24"
              stroke="#dc2626"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>
              Delete Admin
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
              This action cannot be undone
            </div>
          </div>
        </div>

        <p
          style={{
            fontSize: 13,
            color: "var(--text-secondary)",
            lineHeight: 1.6,
            marginBottom: 24,
          }}
        >
          Are you sure you want to delete{" "}
          <strong style={{ color: "var(--text-primary)" }}>{name}</strong>?
        </p>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            onClick={onCancel}
            style={{
              padding: "8px 18px",
              borderRadius: 8,
              border: "1px solid var(--border)",
              background: "transparent",
              color: "var(--text-secondary)",
              fontSize: 13,
              cursor: "pointer",
              fontFamily: "var(--font-body)",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: "8px 18px",
              borderRadius: 8,
              border: "none",
              background: "#dc2626",
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "var(--font-body)",
            }}
          >
            Yes, Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [data, setData] = useState({ content: [], totalElements: 0, totalPages: 0 });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [showReset, setShowReset] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", roleId: "R2" });
  const [newPwd, setNewPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminUsersApi.getAll(page);
      setData(r.data.data);
    } catch {}
    setLoading(false);
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await adminUsersApi.create({
        ...form,
        roleId: toFullRole(form.roleId),
        isActive: false,
      });
      toast.success("Admin created");
      setShowCreate(false);
      setShowPwd(false);
      setForm({ name: "", email: "", password: "", roleId: "R2" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error");
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    try {
      await adminUsersApi.resetPassword(showReset.id, newPwd);
      toast.success("Password reset");
      setShowReset(null);
      setNewPwd("");
    } catch {
      toast.error("Failed");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await adminUsersApi.delete(deleteTarget.id);
      toast.success(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Failed to delete");
      setDeleteTarget(null);
    }
  };

  const filtered = (data.content || []).filter((row) => {
    const q = search.toLowerCase();
    const shortId = toShortId(row.roleId);
    const rowStatus = getStatus(row);
    const matchSearch =
      !q ||
      (row.name || "").toLowerCase().includes(q) ||
      (row.email || "").toLowerCase().includes(q);
    const matchRole = !roleFilter || shortId === roleFilter;
    const matchStatus = !statusFilter || rowStatus === statusFilter;
    return matchSearch && matchRole && matchStatus;
  });

  const stats = {
    total: data.content?.length || 0,
    active: (data.content || []).filter((r) => getStatus(r) === "ACTIVE").length,
    locked: (data.content || []).filter((r) => getStatus(r) === "LOCKED").length,
    inactive: (data.content || []).filter((r) => getStatus(r) === "INACTIVE").length,
    R1: (data.content || []).filter((r) => toShortId(r.roleId) === "R1").length,
    R2: (data.content || []).filter((r) => toShortId(r.roleId) === "R2").length,
    R3: (data.content || []).filter((r) => toShortId(r.roleId) === "R3").length,
  };

  const hasFilters = search || roleFilter || statusFilter;

  const columns = [
    {
      key: "id",
      label: "Sr.",
      render: (v) => {
        const index = data.content.findIndex((item) => item.id === v);
        return (
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "var(--text-muted)",
              minWidth: 28,
              display: "inline-block",
            }}
          >
            {page * 20 + index + 1}
          </span>
        );
      },
    },
    {
      key: "name",
      label: "Admin",
      render: (v, row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar name={v} roleId={row.roleId} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{v || "—"}</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{row.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: "roleId",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span>Role</span>
          <HeaderDropdown
            value={roleFilter}
            onChange={(v) => {
              setRoleFilter(v);
              setPage(0);
            }}
            options={ROLE_OPTIONS}
          />
        </div>
      ),
      render: (v) => {
        const shortId = toShortId(v);
        const fullRole = toFullRole(shortId);
        const Icon = ROLE_ICONS[fullRole];

        return (
          <Badge variant={ROLE_BADGE_VARIANT[shortId] || "default"}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              {Icon ? <Icon size={13} strokeWidth={2} /> : null}
              {ROLE_LABELS[fullRole] || v}
            </span>
          </Badge>
        );
      },
    },
    {
      key: "status",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span>Status</span>
          <HeaderDropdown
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setPage(0);
            }}
            options={STATUS_OPTIONS}
          />
        </div>
      ),
      render: (_, row) => {
        const s = getStatus(row);

        if (s === "LOCKED") {
          return (
            <Badge variant="danger">
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Lock size={13} strokeWidth={2} />
                Locked
              </span>
            </Badge>
          );
        }

        if (s === "ACTIVE") {
          return (
            <Badge variant="success">
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <CheckCircle2 size={13} strokeWidth={2} />
                Active
              </span>
            </Badge>
          );
        }

        return (
          <Badge variant="danger">
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <XCircle size={13} strokeWidth={2} />
              Inactive
            </span>
          </Badge>
        );
      },
    },
    {
      key: "createdAt",
      label: "Joined",
      render: (v) => (
        <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
          {v ? new Date(v).toLocaleDateString("en-IN") : "—"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => {
        const v = row.id;
        const isSuperAdmin = toShortId(row.roleId) === "R1";

        const btnStyle = (disabled) => ({
          width: 30,
          height: 30,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: "transparent",
          border: "1px solid var(--border)",
          borderRadius: 8,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.35 : 1,
          transition: "all 0.15s",
          color: "var(--text-secondary)",
        });

        return (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button
              title={isSuperAdmin ? "Cannot edit Super Admin" : "Edit"}
              onClick={() => !isSuperAdmin && setEditTarget(row)}
              style={btnStyle(isSuperAdmin)}
            >
              <Pencil size={14} strokeWidth={2} />
            </button>

            <button
              title={isSuperAdmin ? "Cannot reset Super Admin password" : "Reset Password"}
              onClick={() => !isSuperAdmin && setShowReset(row)}
              style={btnStyle(isSuperAdmin)}
            >
              <KeyRound size={14} strokeWidth={2} />
            </button>

            <button
              title={isSuperAdmin ? "Cannot delete Super Admin" : "Delete"}
              onClick={() => {
                if (isSuperAdmin) return;
                setDeleteTarget({ id: v, name: row.name });
              }}
              style={btnStyle(isSuperAdmin)}
            >
              <Trash2 size={14} strokeWidth={2} />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <ConfirmDialog
        open={!!deleteTarget}
        name={deleteTarget?.name}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />

      <style>{`
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100%{opacity:0.5} 50%{opacity:1} }
      `}</style>

      <PageHeader
        title="Admin Users"
        subtitle="Manage admin accounts, menu permissions and security policies"
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))",
          gap: 14,
          marginBottom: 28,
        }}
      >
        <StatCard
          title="Total"
          value={stats.total}
          gradient="linear-gradient(135deg,rgba(15,61,62,0.55),rgba(15,61,62,0.25))"
          loading={loading}
        />
        <StatCard
          title="Active"
          value={stats.active}
          gradient="linear-gradient(135deg,rgba(34,197,94,0.55),rgba(34,197,94,0.25))"
          loading={loading}
        />
        <StatCard
          title="Inactive"
          value={stats.inactive}
          gradient="linear-gradient(135deg,rgba(208,179,106,0.6),rgba(208,179,106,0.25))"
          loading={loading}
        />
        <StatCard
          title="Locked"
          value={stats.locked}
          gradient="linear-gradient(135deg,rgba(239,68,68,0.5),rgba(239,68,68,0.2))"
          loading={loading}
        />
        <StatCard
          title="Super Admin"
          value={stats.R1}
          gradient="linear-gradient(135deg,rgba(15,61,62,0.55),rgba(15,61,62,0.25))"
          loading={loading}
        />
        <StatCard
          title="Finance"
          value={stats.R2}
          gradient="linear-gradient(135deg,rgba(59,130,246,0.5),rgba(59,130,246,0.2))"
          loading={loading}
        />
        <StatCard
          title="Support"
          value={stats.R3}
          gradient="linear-gradient(135deg,rgba(107,114,128,0.45),rgba(107,114,128,0.18))"
          loading={loading}
        />
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        <input
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1,
            padding: "9px 14px",
            borderRadius: "var(--radius)",
            border: "1px solid var(--border)",
            background: "var(--bg-input)",
            color: "var(--text-primary)",
            fontSize: 13,
            fontFamily: "var(--font-body)",
            outline: "none",
          }}
        />
        <button
          type="button"
          onClick={() => {
            setSearch("");
            setRoleFilter("");
            setStatusFilter("");
            setPage(0);
          }}
          style={{
            padding: "9px 16px",
            borderRadius: "var(--radius)",
            border: "1px solid var(--border)",
            background: "transparent",
            color: "var(--text-secondary)",
            fontSize: 13,
            cursor: "pointer",
            fontFamily: "var(--font-body)",
          }}
        >
          Clear
        </button>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          + New Admin
        </Button>
      </div>

      <div
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-lg)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "14px 20px",
            borderBottom: "1px solid var(--border)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>
            {filtered.length} admins
          </span>

          {hasFilters && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {search && (
                <span
                  style={{
                    fontSize: 11,
                    background: "rgba(15,61,62,0.1)",
                    color: "#0F3D3E",
                    borderRadius: 20,
                    padding: "2px 10px",
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Search size={12} strokeWidth={2} />
                  {search}
                </span>
              )}

              {roleFilter && (
                <span
                  style={{
                    fontSize: 11,
                    background: "rgba(15,61,62,0.1)",
                    color: "#0F3D3E",
                    borderRadius: 20,
                    padding: "2px 10px",
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {renderRoleIcon(toFullRole(roleFilter), { size: 12, strokeWidth: 2 })}
                  {ROLE_OPTIONS.find((o) => o.value === roleFilter)?.label}
                </span>
              )}

              {statusFilter && (
                <span
                  style={{
                    fontSize: 11,
                    background: "rgba(15,61,62,0.1)",
                    color: "#0F3D3E",
                    borderRadius: 20,
                    padding: "2px 10px",
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Zap size={12} strokeWidth={2} />
                  {statusFilter}
                </span>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
            <div
              style={{
                width: 32,
                height: 32,
                margin: "0 auto 10px",
                border: "3px solid var(--border)",
                borderTop: "3px solid #0F3D3E",
                borderRadius: "50%",
                animation: "spin 0.7s linear infinite",
              }}
            />
            Loading...
          </div>
        ) : (
          <Table columns={columns} data={filtered} emptyText="No admins found" />
        )}

        {(() => {
          const totalPages = data.totalPages;
          if (totalPages <= 1 || data.totalElements === 0) return null;

          const getPages = () => {
            if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);

            const pages = [];
            if (page <= 3) {
              pages.push(0, 1, 2, 3, 4, "...", totalPages - 1);
            } else if (page >= totalPages - 4) {
              pages.push(
                0,
                "...",
                totalPages - 5,
                totalPages - 4,
                totalPages - 3,
                totalPages - 2,
                totalPages - 1
              );
            } else {
              pages.push(0, "...", page - 1, page, page + 1, "...", totalPages - 1);
            }
            return pages;
          };

          const btnStyle = (isActive, isDisabled) => ({
            minWidth: 34,
            height: 34,
            padding: "0 10px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: isActive ? "none" : "1px solid var(--border)",
            borderRadius: 8,
            background: isActive ? "#0F3D3E" : "transparent",
            color: isActive ? "#fff" : isDisabled ? "var(--text-muted)" : "var(--text-primary)",
            fontSize: 13,
            fontWeight: isActive ? 700 : 500,
            cursor: isDisabled ? "not-allowed" : "pointer",
            opacity: isDisabled ? 0.45 : 1,
            transition: "all 0.15s",
            fontFamily: "var(--font-body)",
          });

          return (
            <div
              style={{
                padding: "14px 20px",
                borderTop: "1px solid var(--border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              <button
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
                style={btnStyle(false, page === 0)}
              >
                ← Prev
              </button>

              {getPages().map((pg, i) =>
                pg === "..." ? (
                  <span
                    key={`dots-${i}`}
                    style={{
                      padding: "0 4px",
                      color: "var(--text-muted)",
                      fontSize: 14,
                      userSelect: "none",
                    }}
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={pg}
                    onClick={() => setPage(pg)}
                    style={btnStyle(page === pg, false)}
                    onMouseEnter={(e) => {
                      if (page !== pg) e.currentTarget.style.background = "rgba(15,61,62,0.08)";
                    }}
                    onMouseLeave={(e) => {
                      if (page !== pg) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    {pg + 1}
                  </button>
                )
              )}

              <button
                disabled={page >= totalPages - 1}
                onClick={() => setPage(page + 1)}
                style={btnStyle(false, page >= totalPages - 1)}
              >
                Next →
              </button>
            </div>
          );
        })()}
      </div>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create Admin">
        <form onSubmit={handleCreate} style={{ display: "flex", flexDirection: "column", gap: 13 }}>
          <Input
            label="Full Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
          <Input
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />

          <div style={{ position: "relative" }}>
            <Input
              label="Password"
              type={showPwd ? "text" : "password"}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
            />
            <button
              type="button"
              onClick={() => setShowPwd((p) => !p)}
              title={showPwd ? "Hide password" : "Show password"}
              style={{
                position: "absolute",
                right: 10,
                bottom: 9,
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 4,
                color: "var(--text-muted)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 4,
                transition: "color 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#0F3D3E")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-muted)")}
            >
              {showPwd ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>

          <Select
            label="Role"
            value={form.roleId}
            onChange={(e) => setForm({ ...form, roleId: e.target.value })}
            options={ROLE_SELECT_OPTIONS}
          />

          <div
            style={{
              fontSize: 12,
              color: "var(--text-muted)",
              background: "rgba(15,61,62,0.05)",
              borderRadius: 8,
              padding: "8px 12px",
              border: "1px solid rgba(15,61,62,0.12)",
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Info size={14} strokeWidth={2} />
              <span>
                New admins start as <strong>Inactive</strong>. Activate them from the Edit page after creation.
              </span>
            </span>
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Button variant="ghost" type="button" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button type="submit">Create</Button>
          </div>
        </form>
      </Modal>

      {editTarget && (
        <EditAdminModal admin={editTarget} onClose={() => setEditTarget(null)} onSuccess={load} />
      )}

      <Modal
        open={!!showReset}
        onClose={() => setShowReset(null)}
        title={`Reset Password — ${showReset?.name}`}
      >
        <form onSubmit={handleReset} style={{ display: "flex", flexDirection: "column", gap: 13 }}>
          <Input
            label="New Password"
            type="password"
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            required
          />
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Button variant="ghost" type="button" onClick={() => setShowReset(null)}>
              Cancel
            </Button>
            <Button type="submit">Reset</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}